import React from 'react';
import PropTypes from 'prop-types';
import { Link, Icon } from '@resy/ui-kitchen';
import { handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import { ACQUISITION_CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import ExternalLink from '../../../../../../ui-kitchen/src/svg/icons/external-link.svg';
import './AmexResyCreditNAA.scss';

const AmexResyCreditNAA = ({
  awsFolderName,
  baseRequestUrl,
  matchMedia,
  sectionConfig,
  eventAnalytics,
}) => {
  const sectionId = sectionConfig?.section_id || sectionConfig?.id;
  const logo = sectionConfig?.logo || '';
  const title = sectionConfig?.title || '';
  const copy = sectionConfig?.copy || '';
  const terms = sectionConfig?.fine_print || '';
  const ctaLabel = sectionConfig?.cta?.label;
  const ctaUrl = sectionConfig?.cta?.url || '#';

  const desktopImg = sectionConfig?.image?.desktop || {};
  const mobileImg = sectionConfig?.image?.mobile || {};

  const hasDesktopImg = Boolean(desktopImg.regular && desktopImg.retina);
  const hasMobileImg = Boolean(mobileImg.regular && mobileImg.retina);
  const imagePrefix = `${baseRequestUrl}${awsFolderName}/images/`;
  const sanitizedTitle = sanitizeString(title);
  const sanitizedCopy = sanitizeString(copy);
  const sanitizedTerms = sanitizeString(terms);

  const renderContent = () => (
    <div className="AmexResyCreditNAA__panelBody">
      {logo && (
        <img src={`${imagePrefix}${logo}`} alt="American Express and Resy logo" />
      )}
      <h2
        id={sectionId}
        className="AmexResyCreditNAA__title"
        dangerouslySetInnerHTML={{ __html: sanitizedTitle }} // eslint-disable-line react/no-danger
      />
      {copy && (
        <p
          className="AmexResyCreditNAA__copy"
          dangerouslySetInnerHTML={{ __html: sanitizedCopy }} // eslint-disable-line react/no-danger
        />
      )}
      {terms && (
        <p
          className="AmexResyCreditNAA__terms"
          dangerouslySetInnerHTML={{ __html: sanitizedTerms }} // eslint-disable-line react/no-danger
        />
      )}
      {ctaLabel && ctaUrl && (
        <Link
          className="AmexResyCreditNAA__cta"
          href={ctaUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            handleMktAnalytics(eventAnalytics, ACQUISITION_CTA_EVENT_NAME, {
              action_name: 'NAA - Click',
            });
          }}
        >
          {ctaLabel}
          <Icon component={ExternalLink} role="presentation" />
        </Link>
      )}
    </div>
  );

  return (
    <section className="AmexResyCreditNAA" aria-labelledby={sectionId}>
      <div className="container">
        {!matchMedia.lg ? (
          <div className="AmexResyCreditNAA__mobileWrapper">
            <div className="AmexResyCreditNAA__mobilePanel">{renderContent()}</div>
            {hasMobileImg && (
              <img
                className="AmexResyCreditNAA__mobileImage"
                src={`${imagePrefix}${mobileImg.regular}`}
                srcSet={`${imagePrefix}${mobileImg.regular} 1x, ${imagePrefix}${mobileImg.retina} 2x`}
                alt=""
              />
            )}
          </div>
        ) : (
          <div className="AmexResyCreditNAA__heroBackground">
            {hasDesktopImg && (
              <div className="AmexResyCreditNAA__heroImageLayer">
                <img
                  className="AmexResyCreditNAA__heroPicture"
                  src={`${imagePrefix}${desktopImg.regular}`}
                  srcSet={`${imagePrefix}${desktopImg.regular} 1x, ${imagePrefix}${desktopImg.retina} 2x`}
                  alt=""
                />
              </div>
            )}
            <div className="AmexResyCreditNAA__desktopPanel">{renderContent()}</div>
          </div>
        )}
      </div>
    </section>
  );
};

AmexResyCreditNAA.propTypes = {
  awsFolderName: PropTypes.string.isRequired,
  baseRequestUrl: PropTypes.string.isRequired,
  sectionConfig: PropTypes.shape({
    section_id: PropTypes.string,
    id: PropTypes.string,
    title: PropTypes.string,
    copy: PropTypes.string,
    logo: PropTypes.string,
    fine_print: PropTypes.string,
    image: PropTypes.shape({
      desktop: PropTypes.shape({
        regular: PropTypes.string,
        retina: PropTypes.string,
      }),
      mobile: PropTypes.shape({
        regular: PropTypes.string,
        retina: PropTypes.string,
      }),
    }),
    cta: PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.string,
    }),
  }).isRequired,
  matchMedia: PropTypes.shape({
    lg: PropTypes.bool,
  }).isRequired,
  eventAnalytics: PropTypes.func,
};

AmexResyCreditNAA.defaultProps = {
  eventAnalytics: null,
};

export default AmexResyCreditNAA;
